<template>
  <div v-if="exibir" class="modal-overlay" @click="fecharSeClicouFora">
    <div class="modal-dialog" @click.stop>

      <div class="modal-content">
        <!-- Cabeçalho -->
        <div class="modal-header">
          <h5 class="modal-title">
            <i :class="icone" class="me-2"></i>
            {{ titulo }}
          </h5>
          <button 
            type="button" 
            class="btn-close" 
            @click="responder(false)"
            v-if="botaoFechar"
          ></button>
        </div>

        <!-- Corpo -->
        <div class="modal-body">
          <p class="mb-3">{{ mensagem }}</p>
          <div v-if="detalhes" class="alert alert-info">
            {{ detalhes }}
          </div>
        </div>

        <!-- Rodapé -->
        <div class="modal-footer">
          <button 
            type="button" 
            class="btn btn-sm btn-outline-secondary" 
            @click="responder(false)"
            :disabled="processando"
          >
            <i class="fa fa-times me-1"></i>
            {{ textoNao }}
          </button>

          <button 
            type="button" 
            class="btn-sm"
            :class="classeSimBtn" 
            @click="responder(true)"
            :disabled="processando"
          >

            <i v-if="processando" class="fa fa-spinner fa-spin me-1"></i>
            <i v-else :class="iconeSimBtn" class="me-1"></i>
            {{ textoSim }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

export interface IProps {
  exibir: boolean
  titulo?: string
  mensagem: string
  detalhes?: string
  textoSim?: string
  textoNao?: string
  tipo?: 'warning' | 'danger' | 'success' | 'info' | 'question'
  botaoFechar?: boolean
  fecharSeClicouFora?: boolean
  processando?: boolean
}

const props = withDefaults(defineProps<IProps>(), {
  titulo: 'Confirmação',
  textoSim: 'Sim',
  textoNao: 'Não',
  tipo: 'question',
  botaoFechar: true,
  fecharSeClicouFora: true,
  processando: false
})

const emit = defineEmits<{
  resposta: [confirmou: boolean]
  fechou: []
}>()

// Computed para ícones e classes baseados no tipo
const icone = computed(() => {
  const icones = {
    warning: 'fa fa-exclamation-triangle text-warning',
    danger: 'fa fa-exclamation-circle text-danger',
    success: 'fa fa-check-circle text-success',
    info: 'fa fa-info-circle text-info',
    question: 'fa fa-question-circle text-primary'
  }
  return icones[props.tipo]
})

const classeSimBtn = computed(() => {
  const classes = {
    warning: 'btn btn-warning',
    danger: 'btn btn-danger',
    success: 'btn btn-success',
    info: 'btn btn-info',
    question: 'btn btn-primary'
  }
  return classes[props.tipo]
})

const iconeSimBtn = computed(() => {
  const icones = {
    warning: 'fa fa-check',
    danger: 'fa fa-trash',
    success: 'fa fa-check',
    info: 'fa fa-info',
    question: 'fa fa-check'
  }
  return icones[props.tipo]
})

function responder(confirmou: boolean) {
  if (props.processando) return
  
  emit('resposta', confirmou)
  emit('fechou')
}

function fecharSeClicouFora() {
  if (props.fecharSeClicouFora && !props.processando) {
    responder(false)
  }
}

// Impedir scroll do body quando modal estiver aberto
document.body.style.overflow = props.exibir ? 'hidden' : 'auto'
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1055;
  backdrop-filter: blur(2px);
}

.modal-dialog {
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  animation: modalSlideIn 0.3s ease-out;
}

.modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.modal-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.modal-body {
  padding: 1.5rem;
}

.modal-body p {
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 0 0 8px 8px;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  transition: opacity 0.15s ease-in-out;
}

.btn-close:hover {
  opacity: 1;
}

.btn-close::before {
  content: "×";
  font-size: 1.5rem;
  line-height: 1;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Responsividade */
@media (max-width: 576px) {
  .modal-dialog {
    width: 95%;
    margin: 1rem;
  }
  
  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 1rem;
  }
  
  .modal-footer {
    flex-direction: column;
  }
  
  .modal-footer .btn {
    width: 100%;
  }
}
</style>